import {
  type FixedDepartureDetail,
  type FixedDepartureMarkupType,
  type FixedDepartureSupplementBasis,
  type FixedDepartureSupplementKind,
  type FixedDepartureVehicleSupplementOut,
} from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzDatePicker,
  UzInput,
  UzMoneyInput,
  UzNumberInput,
  UzSelect,
} from '@utazzo/ui';
import { Car, Plus, Sparkles, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  useCreateFixedDepartureVehicleSupplement,
  useDeleteFixedDepartureVehicleSupplement,
  useSaveFixedDepartureLandPackage,
  useUpdateFixedDepartureVehicleSupplement,
} from '../use-fixed-departures';

import { useVehicleTypesList } from '@/features/vehicle-types/use-vehicle-types';

const NONE = '__none__';

const paiseToRupees = (paise: string | null | undefined): number =>
  paise === null || paise === undefined ? 0 : Number(paise) / 100;
const rupeesToPaise = (rupees: number | null): string => String(Math.round((rupees ?? 0) * 100));

const MARKUP_OPTIONS = [
  { value: 'FIXED', label: 'Fixed amount' },
  { value: 'PERCENTAGE', label: 'Percentage' },
] as const;

// Vehicle supplements speak the vendor's PRIVATE/SIC language.
const BASIS_OPTIONS = [
  { value: 'PER_TOTAL', label: 'Private (per vehicle x quantity)' },
  { value: 'PER_PERSON', label: 'SIC (per adult + per child)' },
] as const;

// "Other" supplements use plain language: a flat charge or a per-head charge.
const OTHER_BASIS_OPTIONS = [
  { value: 'PER_PERSON', label: 'Per person' },
  { value: 'PER_TOTAL', label: 'Fixed' },
] as const;

const KIND_OPTIONS = [
  { value: 'VEHICLE', label: 'Vehicle / transfer' },
  { value: 'VISA', label: 'Visa' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'OTHER', label: 'Other' },
] as const;

const KIND_LABEL: Record<FixedDepartureSupplementKind, string> = {
  VEHICLE: 'Vehicle',
  VISA: 'Visa',
  INSURANCE: 'Insurance',
  OTHER: 'Other',
};

interface RateDraft {
  markupType: FixedDepartureMarkupType;
  markupAmount: number;
  markupPercent: number;
  validFrom: string;
  validTo: string;
}

const toRateDraft = (fd: FixedDepartureDetail): RateDraft => {
  const lp = fd.landPackage;
  return {
    markupType: lp?.markupType ?? 'FIXED',
    markupAmount: paiseToRupees(lp?.markupPaise),
    markupPercent: lp?.markupPercent === null || !lp ? 0 : Number(lp.markupPercent ?? 0),
    validFrom: lp?.validFrom ?? '',
    validTo: lp?.validTo ?? '',
  };
};

interface SupplementDraft {
  kind: FixedDepartureSupplementKind;
  vehicleTypeId: string;
  label: string;
  condition: string;
  /** PRIVATE (PER_TOTAL): the cost of one vehicle, in rupees. */
  amount: number;
  /** SIC (PER_PERSON): per-adult / per-child rates, in rupees. */
  adultPrice: number;
  childPrice: number;
  basis: FixedDepartureSupplementBasis;
}

const emptySupplement = (): SupplementDraft => ({
  kind: 'VEHICLE',
  vehicleTypeId: NONE,
  label: '',
  condition: '',
  amount: 0,
  adultPrice: 0,
  childPrice: 0,
  basis: 'PER_TOTAL',
});

const toSupplementDraft = (s: FixedDepartureVehicleSupplementOut): SupplementDraft => ({
  kind: s.kind,
  vehicleTypeId: s.vehicleTypeId ?? NONE,
  label: s.label ?? '',
  condition: s.condition ?? '',
  amount: paiseToRupees(s.amountPaise),
  adultPrice: paiseToRupees(s.adultPricePaise),
  childPrice: paiseToRupees(s.childPricePaise),
  basis: s.basis,
});

const toSupplementPayload = (d: SupplementDraft) => ({
  kind: d.kind,
  // A vehicle type only belongs on a VEHICLE supplement.
  vehicleTypeId: d.kind === 'VEHICLE' && d.vehicleTypeId !== NONE ? d.vehicleTypeId : null,
  label: d.label.trim() || null,
  condition: d.condition.trim() || null,
  // PRIVATE keeps the per-vehicle amount; SIC keeps the per-pax rates. The
  // unused ones are sent as 0 so switching basis doesn't carry stale money.
  amountPaise: rupeesToPaise(d.basis === 'PER_TOTAL' ? d.amount : 0),
  adultPricePaise: rupeesToPaise(d.basis === 'PER_PERSON' ? d.adultPrice : 0),
  childPricePaise: rupeesToPaise(d.basis === 'PER_PERSON' ? d.childPrice : 0),
  basis: d.basis,
});

/** Minor-units string -> "$1,234" / "₹1,234" for compact list display, in the
 *  FD's own currency (vendor sheets are often USD/EUR, not INR). */
const money = (paise: string, currencyCode: string): string =>
  (Number(paise) / 100).toLocaleString('en-IN', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 0,
  });

const ymdToDate = (v: string): Date | undefined => (v ? new Date(`${v}T00:00:00.000Z`) : undefined);
const dateToYmd = (d: Date | null | undefined): string =>
  d ? new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10) : '';

/**
 * The rate block: our markup, the contracted validity window, and the vendor's
 * conditional vehicle supplements.
 *
 * The PRICES are not here - a vendor quotes a grid (tier x group size), which
 * lives on the Tiers & Pricing step.
 */
export const FdLandPackageStep = ({ fd }: { fd: FixedDepartureDetail }): React.JSX.Element => {
  const save = useSaveFixedDepartureLandPackage(fd.id);
  const createSupplement = useCreateFixedDepartureVehicleSupplement(fd.id);
  const updateSupplement = useUpdateFixedDepartureVehicleSupplement(fd.id);
  const deleteSupplement = useDeleteFixedDepartureVehicleSupplement(fd.id);
  const vehicleTypes = useVehicleTypesList({
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    isActive: true,
  });

  // The FD carries its own currency (vendor sheets are often USD/EUR); every
  // money field + list label follows it instead of hardcoding INR.
  const currencyCode = fd.currencyCode ?? undefined;
  const displayCurrency = fd.currencyCode ?? 'INR';
  const [draft, setDraft] = useState<RateDraft>(() => toRateDraft(fd));
  const [adding, setAdding] = useState(false);
  const [supDraft, setSupDraft] = useState<SupplementDraft>(emptySupplement);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSupDraft, setEditSupDraft] = useState<SupplementDraft>(emptySupplement);

  useEffect(() => {
    setDraft(toRateDraft(fd));
  }, [fd]);

  const set = (patch: Partial<RateDraft>): void => setDraft((p) => ({ ...p, ...patch }));

  const vehicleOptions = [
    { value: NONE, label: 'No vehicle type' },
    ...(vehicleTypes.data?.data ?? []).map((v) => ({ value: v.id, label: v.name })),
  ];

  const datesInverted = Boolean(
    draft.validFrom && draft.validTo && draft.validFrom > draft.validTo,
  );

  const supplementValid = (d: SupplementDraft): boolean =>
    d.kind === 'VEHICLE'
      ? d.vehicleTypeId !== NONE || d.label.trim().length > 0
      : d.label.trim().length > 0;

  const submitRate = async (): Promise<void> => {
    await save.mutateAsync({
      markupType: draft.markupType,
      markupPaise: draft.markupType === 'FIXED' ? rupeesToPaise(draft.markupAmount) : null,
      markupPercent: draft.markupType === 'PERCENTAGE' ? String(draft.markupPercent) : null,
      validFrom: draft.validFrom || null,
      validTo: draft.validTo || null,
    });
  };

  const submitNewSupplement = async (): Promise<void> => {
    if (!supplementValid(supDraft)) return;
    await createSupplement.mutateAsync(toSupplementPayload(supDraft));
    setSupDraft(emptySupplement());
    setAdding(false);
  };

  const submitEditSupplement = async (): Promise<void> => {
    if (!editingId || !supplementValid(editSupDraft)) return;
    await updateSupplement.mutateAsync({
      supplementId: editingId,
      payload: toSupplementPayload(editSupDraft),
    });
    setEditingId(null);
  };

  const supplementForm = (
    d: SupplementDraft,
    setD: (patch: Partial<SupplementDraft>) => void,
  ): React.JSX.Element => (
    <div className="space-y-3">
      {(() => {
        const showVehicleType = d.kind === 'VEHICLE';
        // Visa / insurance are always per adult + per child, so they show no
        // "Charged" control at all; vehicle + other keep the choice.
        const showCharged = d.kind === 'VEHICLE' || d.kind === 'OTHER';
        // Label + Condition grow to fill the space freed when vehicle type
        // and/or the Charged control are hidden (12-col grid on lg).
        const wideSpan = showVehicleType
          ? 'lg:col-span-3'
          : showCharged
            ? 'lg:col-span-4'
            : 'lg:col-span-5';
        return (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-12">
            <div className="lg:col-span-2">
              <UzSelect<FixedDepartureSupplementKind>
                label="Type"
                value={d.kind}
                onValueChange={(v) =>
                  // Visa/insurance force per-pax; any non-vehicle kind clears the
                  // (now irrelevant) vehicle type.
                  setD(
                    v === 'VISA' || v === 'INSURANCE'
                      ? { kind: v, vehicleTypeId: NONE, basis: 'PER_PERSON' }
                      : v === 'VEHICLE'
                        ? { kind: v }
                        : { kind: v, vehicleTypeId: NONE },
                  )
                }
                options={KIND_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              />
            </div>
            {showVehicleType && (
              <div className="lg:col-span-2">
                <UzSelect
                  label="Vehicle type"
                  value={d.vehicleTypeId}
                  onValueChange={(v) => setD({ vehicleTypeId: v })}
                  options={vehicleOptions}
                />
              </div>
            )}
            <div className={wideSpan}>
              <UzInput
                label="Label"
                placeholder={
                  d.kind === 'VEHICLE' ? 'e.g. 14-Seater Tempo' : 'e.g. Schengen Visa Fee'
                }
                value={d.label}
                onChange={(e) => setD({ label: e.target.value })}
              />
            </div>
            <div className={wideSpan}>
              <UzInput
                label="Condition"
                placeholder="e.g. If pickup from Jammu / Katra"
                value={d.condition}
                onChange={(e) => setD({ condition: e.target.value })}
              />
            </div>
            {showCharged && (
              <div className="lg:col-span-2">
                <UzSelect<FixedDepartureSupplementBasis>
                  label="Charged"
                  value={d.basis}
                  onValueChange={(v) => setD({ basis: v })}
                  options={(d.kind === 'VEHICLE' ? BASIS_OPTIONS : OTHER_BASIS_OPTIONS).map(
                    (o) => ({
                      value: o.value,
                      label: o.label,
                    }),
                  )}
                />
              </div>
            )}
          </div>
        );
      })()}
      {d.basis === 'PER_TOTAL' ? (
        <UzMoneyInput
          label={
            d.kind === 'VEHICLE'
              ? 'Per-vehicle cost (agent picks quantity per trip)'
              : 'Fixed amount'
          }
          currency={currencyCode}
          value={d.amount}
          onValueChange={(v) => setD({ amount: v ?? 0 })}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <UzMoneyInput
            label="Per adult (12+)"
            currency={currencyCode}
            value={d.adultPrice}
            onValueChange={(v) => setD({ adultPrice: v ?? 0 })}
          />
          <UzMoneyInput
            label="Per child (2-12)"
            currency={currencyCode}
            value={d.childPrice}
            onValueChange={(v) => setD({ childPrice: v ?? 0 })}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-zinc-900">Markup</h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          Applied on top of the vendor prices entered on the Tiers &amp; Pricing step.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <UzSelect<FixedDepartureMarkupType>
            label="Markup type"
            value={draft.markupType}
            onValueChange={(v) => set({ markupType: v })}
            options={MARKUP_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
          {draft.markupType === 'FIXED' ? (
            <UzMoneyInput
              label="Markup amount"
              currency={currencyCode}
              value={draft.markupAmount}
              onValueChange={(v) => set({ markupAmount: v ?? 0 })}
            />
          ) : (
            <UzNumberInput
              label="Markup percent"
              precision={2}
              min={0}
              max={999.99}
              value={draft.markupPercent}
              onValueChange={(v) => set({ markupPercent: v ?? 0 })}
            />
          )}
        </div>
      </section>

      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-zinc-900">Rate validity</h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          The window this contracted rate applies to, e.g. 01 Jul - 30 Sep 2026. The fixed departure
          itself stays undated.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="mb-1 text-xs font-medium text-zinc-700">Valid from</p>
            <UzDatePicker
              displayFormat="dd-MM-yyyy"
              placeholder="Valid from"
              value={ymdToDate(draft.validFrom)}
              onValueChange={(d) => set({ validFrom: dateToYmd(d) })}
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-zinc-700">Valid to</p>
            <UzDatePicker
              displayFormat="dd-MM-yyyy"
              placeholder="Valid to"
              value={ymdToDate(draft.validTo)}
              onValueChange={(d) => set({ validTo: dateToYmd(d) })}
            />
          </div>
        </div>
        {datesInverted && (
          <UzAlert tone="destructive" className="mt-4">
            Valid from must be on or before valid to.
          </UzAlert>
        )}
      </section>

      <UzButton onClick={submitRate} loading={save.isPending} disabled={datesInverted}>
        Save rate block
      </UzButton>

      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Supplements</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              Priced add-ons the operator can tick while building from this FD - a vehicle upgrade,
              a visa fee, insurance, or anything else. Vehicle supplements join the transfers; the
              rest become add-on inclusion lines.
            </p>
          </div>
          {!adding && (
            <UzButton size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              Add supplement
            </UzButton>
          )}
        </div>

        {adding && (
          <div className="mt-4 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50 p-4">
            {supplementForm(supDraft, (patch) => setSupDraft((p) => ({ ...p, ...patch })))}
            <div className="mt-3 flex items-center gap-2">
              <UzButton
                size="sm"
                onClick={submitNewSupplement}
                disabled={!supplementValid(supDraft)}
                loading={createSupplement.isPending}
              >
                Add supplement
              </UzButton>
              <UzButton
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAdding(false);
                  setSupDraft(emptySupplement());
                }}
              >
                Cancel
              </UzButton>
            </div>
          </div>
        )}

        <div className="mt-4 space-y-2">
          {fd.vehicleSupplements.length === 0 && !adding && (
            <p className="text-sm text-zinc-400">No supplements added.</p>
          )}
          {fd.vehicleSupplements.map((supplement) =>
            editingId === supplement.id ? (
              <div key={supplement.id} className="rounded-lg border border-zinc-300 p-4">
                {supplementForm(editSupDraft, (patch) =>
                  setEditSupDraft((p) => ({ ...p, ...patch })),
                )}
                <div className="mt-3 flex items-center gap-2">
                  <UzButton
                    size="sm"
                    onClick={submitEditSupplement}
                    disabled={!supplementValid(editSupDraft)}
                    loading={updateSupplement.isPending}
                  >
                    Save
                  </UzButton>
                  <UzButton size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                    Cancel
                  </UzButton>
                </div>
              </div>
            ) : (
              <div
                key={supplement.id}
                className="flex items-center gap-3 rounded-lg border border-zinc-200 px-4 py-2.5"
              >
                {supplement.kind === 'VEHICLE' ? (
                  <Car className="h-4 w-4 shrink-0 text-zinc-300" aria-hidden />
                ) : (
                  <Sparkles className="h-4 w-4 shrink-0 text-rose-300" aria-hidden />
                )}
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => {
                    setEditingId(supplement.id);
                    setEditSupDraft(toSupplementDraft(supplement));
                  }}
                >
                  <div className="truncate text-sm font-medium text-zinc-900">
                    {supplement.kind !== 'VEHICLE' && (
                      <span className="mr-1.5 rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-rose-600 uppercase">
                        {KIND_LABEL[supplement.kind]}
                      </span>
                    )}
                    {supplement.vehicleTypeName ?? supplement.label ?? 'Supplement'}
                    {' - '}
                    {supplement.basis === 'PER_TOTAL'
                      ? `${money(supplement.amountPaise, displayCurrency)} / vehicle`
                      : `${money(supplement.adultPricePaise, displayCurrency)} adult, ${money(supplement.childPricePaise, displayCurrency)} child`}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
                    <span>
                      {supplement.basis === 'PER_TOTAL'
                        ? 'Private (per vehicle x quantity)'
                        : 'SIC (per adult + per child)'}
                    </span>
                    {supplement.condition && (
                      <>
                        <span className="text-zinc-300">|</span>
                        <span>{supplement.condition}</span>
                      </>
                    )}
                  </div>
                </button>
                <UzButton
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-zinc-400 hover:text-red-600"
                  onClick={() => deleteSupplement.mutate(supplement.id)}
                  aria-label="Remove supplement"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </UzButton>
              </div>
            ),
          )}
        </div>
      </section>
    </div>
  );
};
